import { useMemo } from "react";
import {
  KEBAB_CHART_SIZE,
  formatKebabChartDay,
  kebabBarPlot,
  kebabChartX,
  kebabChartY,
  kebabChartYTicks,
  kebabCumulativeSeries,
  kebabLocalIsoDay,
  type KebabContestant,
  type KebabEntry,
} from "@ziolo/shared";
import { Card } from "@ziolo/ui";

const SIZE = KEBAB_CHART_SIZE;

export function KebabCharts({
  contestants,
  entries,
  startDate,
  endDate,
  now,
}: {
  contestants: KebabContestant[];
  entries: KebabEntry[];
  startDate: string;
  endDate: string;
  now: number;
}) {
  const empty = contestants.every((contestant) => contestant.count === 0);
  const today = kebabLocalIsoDay(new Date(now));
  const series = useMemo(
    () =>
      kebabCumulativeSeries(
        contestants,
        entries,
        startDate,
        endDate,
        new Date(`${today}T12:00:00`),
      ),
    [contestants, entries, startDate, endDate, today],
  );
  const bars = useMemo(() => kebabBarPlot(contestants), [contestants]);

  return (
    <>
      <Card title="Łączna liczba kebabów" className="kb-chart">
        {empty ? (
          <p className="kb-empty">Na razie zero. Ktoś musi zacząć.</p>
        ) : (
          <BarChart plot={bars} />
        )}
      </Card>
      <Card title="Postęp w czasie" className="kb-chart">
        {empty ? (
          <p className="kb-empty">Na razie zero. Ktoś musi zacząć.</p>
        ) : (
          <LineChart
            series={series}
            startDate={startDate}
            endDate={endDate}
            today={today}
            max={bars.max}
          />
        )}
      </Card>
    </>
  );
}

function BarChart({ plot }: { plot: ReturnType<typeof kebabBarPlot> }) {
  const ticks = kebabChartYTicks(plot.max);
  const axisY = SIZE.padTop + plot.plotHeight;
  const summary = plot.bars.map((bar) => `${bar.name} ${bar.count}`).join(", ");

  return (
    <svg
      className="kb-chart__svg"
      viewBox={`0 0 ${SIZE.width} ${SIZE.height}`}
      role="img"
      aria-label={`Łączna liczba kebabów: ${summary}`}
    >
      {ticks.map((tick) => {
        const y = kebabChartY(tick, plot.max);
        return (
          <g key={tick}>
            <line
              className="kb-chart__grid"
              x1={SIZE.padLeft}
              x2={SIZE.width - SIZE.padRight}
              y1={y}
              y2={y}
            />
            <text className="kb-chart__label" x={SIZE.padLeft - 8} y={y} textAnchor="end" dy="0.35em">
              {tick}
            </text>
          </g>
        );
      })}
      {plot.bars.map((bar) => (
        <g key={bar.id}>
          {bar.height > 0 ? (
            <rect
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.height}
              fill={bar.color}
              rx="4"
              ry="4"
            />
          ) : null}
          {bar.count > 0 ? (
            <text
              className="kb-chart__value"
              x={bar.x + bar.width / 2}
              y={bar.y - 8}
              textAnchor="middle"
            >
              {bar.count}
            </text>
          ) : null}
          <text
            className="kb-chart__name"
            x={bar.x + bar.width / 2}
            y={axisY + 18}
            textAnchor="middle"
          >
            {bar.name}
          </text>
        </g>
      ))}
    </svg>
  );
}

function LineChart({
  series,
  startDate,
  endDate,
  today,
  max,
}: {
  series: ReturnType<typeof kebabCumulativeSeries>;
  startDate: string;
  endDate: string;
  today: string;
  max: number;
}) {
  const ticks = kebabChartYTicks(max);
  const xTicks = [startDate, midDay(startDate, endDate), endDate];
  const todayX = kebabChartX(today, startDate, endDate);
  const axisY = kebabChartY(0, max);
  const drawn = series.filter((item) => item.points.some((point) => point.count > 0));

  return (
    <div>
      <svg
        className="kb-chart__svg"
        viewBox={`0 0 ${SIZE.width} ${SIZE.height}`}
        role="img"
        aria-label="Postęp w czasie"
      >
        {ticks.map((tick) => {
          const y = kebabChartY(tick, max);
          return (
            <g key={tick}>
              <line
                className="kb-chart__grid"
                x1={SIZE.padLeft}
                x2={SIZE.width - SIZE.padRight}
                y1={y}
                y2={y}
              />
              <text className="kb-chart__label" x={SIZE.padLeft - 8} y={y} textAnchor="end" dy="0.35em">
                {tick}
              </text>
            </g>
          );
        })}
        {today >= startDate && today <= endDate ? (
          <g>
            <line
              className="kb-chart__today"
              x1={todayX}
              x2={todayX}
              y1={SIZE.padTop}
              y2={axisY}
            />
            <text className="kb-chart__label" x={todayX} y={SIZE.padTop - 8} textAnchor="middle">
              dziś
            </text>
          </g>
        ) : null}
        {drawn.map((item) => {
          const coords = item.points.map((point) => {
            const x = kebabChartX(point.day, startDate, endDate);
            const y = kebabChartY(point.count, max);
            return `${x},${y}`;
          });
          const last = item.points.at(-1);
          const lastX = last ? kebabChartX(last.day, startDate, endDate) : 0;
          const lastY = last ? kebabChartY(last.count, max) : 0;
          return (
            <g key={item.contestantId}>
              <polyline className="kb-chart__line" points={coords.join(" ")} stroke={item.color} />
              {last && last.count > 0 ? (
                <circle cx={lastX} cy={lastY} r="3.5" fill={item.color} />
              ) : null}
            </g>
          );
        })}
        {xTicks.map((day) => (
          <text
            key={day}
            className="kb-chart__name"
            x={kebabChartX(day, startDate, endDate)}
            y={axisY + 18}
            textAnchor="middle"
          >
            {formatKebabChartDay(day)}
          </text>
        ))}
      </svg>
      <ul className="kb-chart__legend">
        {series.map((item) => (
          <li key={item.contestantId}>
            <span className="kb-chart__swatch" style={{ background: item.color }} />
            {item.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

function midDay(from: string, to: string): string {
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  const mid = new Date((fromMs + toMs) / 2);
  const month = String(mid.getUTCMonth() + 1).padStart(2, "0");
  const day = String(mid.getUTCDate()).padStart(2, "0");
  return `${mid.getUTCFullYear()}-${month}-${day}`;
}
